import {Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, OnChanges} from '@angular/core';
import { StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import {Observable, of, Subject, defer, combineLatest, BehaviorSubject} from 'rxjs';
import { takeUntil, catchError, tap, distinctUntilChanged, map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { SessionPersistenceService } from '../../session-manager/session-persistence.service';
import { MicroTestMetadataCreationDetails, TestIdService, TestWithMetadata, MicroTestMetadata } from '../../test/test-id.service';
import {DynamicRefinedTestCase} from '../../../../shared/templateGen/templateInterfaces';
import {MacroScenarioItem, MicroScenarioSpecificationModel} from '../three-column-workspace/three-column-workspace.component';
import {LayoutService} from '../../../../shared/services/layout.service';

@Component({
  selector: 'app-micro-scenario-test-generator',
  templateUrl: './micro-scenario-test-generator.component.html',
  styleUrls: ['./micro-scenario-test-generator.component.scss']
})
export class MicroScenarioTestGeneratorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() macroScenario: MacroScenarioItem;
  @Input() microScenarioSpec: MicroScenarioSpecificationModel;
  @Input() refinedStoryContext: string; // Non usato direttamente per criterionTextForApi nel nuovo formato
  @Output() generateTestsRequest = new EventEmitter<void>();
  private destroy$: Subject<void> = new Subject<void>();

  selectedModel = '';
  selectedTemplate = '';
  istruzioniScriviTest = '';

  templates: any[] = [];
  templateNames: string[] = [];
  rawTestsForDisplay: TestWithMetadata[] = []; // Per la tabella dei test grezzi
  rawTestsColumns: string[] = [];  // Colonne per la tabella dei test grezzi
  private relevantRawTests: TestWithMetadata[] = []; // Per la logica interna
  testCount = 0;
  hasSubtable = false;

  isGeneratingTests = false;
  // NUOVO: Proprietà per i test consolidati
  consolidatedTests: TestWithMetadata[] = [];
  private microScenarioIdSubject = new BehaviorSubject<string | null>(null);
  isEditModalVisible = false;
  testsForModal: any[] = [];
  columnsForModalEditing: string[] = []; // To pass to the modal
  consolidatedTestsForDisplay: TestWithMetadata[] = []; // <-- NOME CORRETTO
  consolidatedColumns: string[] = [];
  private uid: string | null = null;
  private projectId: string;
  private projectLanguage = 'en';
  private currentSessionNameFromState: string | null = null;

  allSubTablesExpanded = false;

  // NEW: Full-screen modal properties
  isFullScreenModalVisible = false;

  private memoizedSubTableColumns = new Map<string, string[]>();
  private memoizedSubTableRows = new Map<string, any[]>();
// La gestione dei test grezzi tramite @Input rimane, ma ora dobbiamo anche
  // ascoltare i cambiamenti globali sia per i test grezzi che per quelli consolidati.
  @Input()
  set existingTests(value: TestWithMetadata[]) {
    this.processExistingRawTests(value);
  }

  constructor(
    private sessioneService: StatoSessioneService,
    private toastr: ToastrService,
    private sessionPersistenceService: SessionPersistenceService,
    private testIdService: TestIdService,
    public cd: ChangeDetectorRef,
    private layoutService: LayoutService
  ) {}

  ngOnInit(): void {
    this.setupSubscriptions();
    // Initialize the micro scenario ID tracker
    if (this.microScenarioSpec?.id) {
      this.microScenarioIdSubject.next(this.microScenarioSpec.id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Track when the micro scenario changes
    if (changes['microScenarioSpec']) {
      const newId = changes['microScenarioSpec'].currentValue?.id;
      // // console.log('Micro scenario input changed to:', newId);

      if (newId && newId !== this.microScenarioIdSubject.value) {
        this.microScenarioIdSubject.next(newId);
      }
    }
  }

  private processExistingRawTests(rawTests: TestWithMetadata[] = []): void {
    this.relevantRawTests = rawTests;
    this.testCount = rawTests.length;

    // *** THE DEFINITIVE FIX: Keep the full TestWithMetadata object for the template ***
    if (JSON.stringify(this.rawTestsForDisplay) !== JSON.stringify(rawTests)) {
      this.rawTestsForDisplay = rawTests; // Assign the full array with metadata

      if (this.rawTestsForDisplay.length > 0) {
        // Get all keys from the 'data' property of the first test
        const keys = Object.keys(this.rawTestsForDisplay[0].data);

        // Filter out any key that is 'ID' (case-insensitive) from the original data keys.
        // This prevents the data's own ID from being considered for a column header.
        const filteredKeys = keys.filter(k => k.toUpperCase() !== 'ID');

        // Now, manually add our special 'ID' column to the very beginning of the headers list.
        // The template will use this to render the ID from metadata.
        this.rawTestsColumns = ['ID', ...filteredKeys];
      } else {
        this.rawTestsColumns = [];
      }
      this.cd.detectChanges();
    }
  }

  private setupSubscriptions(): void {
    // Sottoscrizioni per le impostazioni generali del progetto (queste sono corrette)
    this.sessioneService.projectId$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(pid => this.projectId = pid);

    this.sessioneService.projectLanguage$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(lang => this.projectLanguage = lang || 'en');

    this.sessioneService.fileName$
      .pipe(takeUntil(this.destroy$))
      .subscribe(fileName => this.currentSessionNameFromState = fileName);

    this.sessioneService.selectedTemplate$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(templateName => {
        if (this.selectedTemplate !== templateName) {
          this.selectedTemplate = templateName;
          this.checkForSubtable();
        }
      });

    this.sessioneService.istruzioniScriviTest$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(instructions => this.istruzioniScriviTest = instructions);

    this.sessioneService.templates$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tpls => {
        this.templates = tpls || [];
        this.templateNames = this.templates.map(template => template.templateName);
        this.checkForSubtable();
      });

    // Main data subscription with micro scenario change detection
    combineLatest([
      this.sessioneService.generatedTests$,
      this.sessioneService.globalConsolidatedTests$,
      this.microScenarioIdSubject.pipe(distinctUntilChanged())
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([allRawTests, allConsolidatedTests, currentMicroId]) => {
      // console.log(`Processing data for micro scenario: ${currentMicroId}`);

      if (this.microScenarioSpec && currentMicroId) {
        // 1. Filter raw tests
        const trueOriginalId = this.getTrueOriginalIdForTestOperations();
        const relevantRawTests = (allRawTests || []).filter(
          t => t.metadata?.microScenarioId === trueOriginalId
        );

        // 2. Update raw tests UI
        this.processExistingRawTests(relevantRawTests);

        // 3. Update consolidated tests UI
        this.processConsolidatedTests(this.relevantRawTests, allConsolidatedTests || []);

        // console.log(`[${currentMicroId}] Updated: ${this.relevantRawTests.length} raw, ${this.consolidatedTestsForDisplay.length} consolidated`);
      }
    });
  }

  checkForSubtable(): void {
    let newHasSubtable = false;
    if (this.selectedTemplate && this.templates.length > 0) {
      const templateObj = this.templates.find(t => t.templateName === this.selectedTemplate);
      newHasSubtable = templateObj ? this.templateHasSubtableLogic(templateObj) : false;
    }
    if (this.hasSubtable !== newHasSubtable) {
      this.hasSubtable = newHasSubtable;
      this.cd.detectChanges();
    }
  }

  private templateHasSubtableLogic(template: any): boolean {
    if (template && template.columns && Array.isArray(template.columns)) {
      return template.columns.some(column =>
        column.list && Array.isArray(column.subColumns) && column.subColumns.length > 0
      );
    }
    return false;
  }

  clearGeneratedTests(): void {
    const microIdForLogs = this.microScenarioSpec?.id || 'unknown_micro_clear';
    const microSpecNameShort = `"${(this.microScenarioSpec?.specification || 'N/A').substring(0, 30)}..."`;
    const trueOriginalId = (this.microScenarioSpec.isDuplicateContent && this.microScenarioSpec.originalMicroScenarioId)
      ? this.microScenarioSpec.originalMicroScenarioId
      : this.microScenarioSpec.id;

    // For a duplicate view, clearing should still target the trueOriginalId in the session.
    // The Swal message should reflect this.
    Swal.fire({
      title: 'Delete Associated Tests?',
      html: `Are you sure you want to delete all tests associated with the ID <strong>${trueOriginalId}</strong> from the session?
         <br><br>This micro-scenario (${microSpecNameShort}, ID: ${this.microScenarioSpec.id}) ${this.microScenarioSpec.isDuplicateContent ? 'is a duplicate and displays these tests.' : 'is the original for these tests.'}
         <br><br>This action is irreversible.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel' // Standard English practice
    }).then((result) => {
      if (result.isConfirmed) {
        this.toastr.info(`Deleting tests for ID ${trueOriginalId}...`);

        // Renamed 'sessioneService' to 'sessioneService' for clarity in English
        const currentGlobalTests = this.sessioneService.getCurrentGeneratedTests();
        const remainingGlobalTests = currentGlobalTests.filter(
          test => test.metadata.microScenarioId !== trueOriginalId // Logic remains the same
        );

        if (remainingGlobalTests.length < currentGlobalTests.length) {
          // Update the session with the filtered list of tests
          this.sessioneService.updateGeneratedTests(this.testIdService.sortTests(remainingGlobalTests));

          // The log message is now in English
          this.saveSessionAfterChange(`Tests deleted for micro-scenario ID ${trueOriginalId}`);

          this.toastr.success(`Tests associated with ID ${trueOriginalId} were successfully deleted from the session.`);
        } else {
          // The toastr title is now in English
          this.toastr.info(`No tests found for ID ${trueOriginalId} to delete.`, 'No Action Taken');
        }

        // Explicitly update this component's view after the deletion
        const allLatestGlobalTests = this.sessioneService.getCurrentGeneratedTests();
        const relevantTestsForThisInstance = allLatestGlobalTests.filter(
          t => t.metadata && t.metadata.microScenarioId === trueOriginalId
        );
        this.processExistingRawTests(relevantTestsForThisInstance);
      }
    });
  }

  private saveSessionAfterChange(reason: string): void {
    this.sessionPersistenceService.saveCurrentSession(`tests`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (saveResponse: any) => {
          this.toastr.success(`Session saved!`, 'Session Saved');
        },
        error: (err) => {
          // console.error(`Error saving session after ${reason}:`, err);
          this.toastr.error('Failed to save session.', 'Save Error');
        }
      });
  }

  public onGenerateTestsButtonClick(): void {
    if (this.isGeneratingTests) {
      this.toastr.info('Test generation already in progress for this item.');
      return;
    }
    // The child's only job is to tell the parent to start the process.
    // All confirmation dialogs and logic will now be handled by the parent.
    this.generateTestsRequest.emit();
  }

  // NEW: Full-screen modal methods
  openFullScreenModal(): void {
    if (this.rawTestsForDisplay.length === 0 && this.consolidatedTestsForDisplay.length === 0) {
      this.toastr.info('No tests to display in full-screen view.', 'No Data');
      return;
    }

    this.isFullScreenModalVisible = true;
    this.layoutService.forceSidebarHide(true);
    document.body.style.overflow = 'hidden';
    this.cd.detectChanges();
  }

  closeFullScreenModal(): void {
    this.isFullScreenModalVisible = false;
    this.layoutService.forceSidebarHide(false);
    document.body.style.overflow = '';
    this.cd.detectChanges();
  }

  onFullScreenModalBackdropClick(event: MouseEvent): void {
    // Close modal only if clicking the backdrop (not the modal content)
    if (event.target === event.currentTarget) {
      this.closeFullScreenModal();
    }
  }

  isSubTable(value: any): boolean {
    return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0]);
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  getSubTableRowCount(subTableData: any): number {
    if (Array.isArray(subTableData)) {
      return subTableData.length;
    }
    return 0;
  }

  trackByTestId(index: number, test: any): any { // test è l'oggetto con { ...data, _frontendDisplayId_ }
    return test._frontendDisplayId_ || index; // Usa l'ID frontend per il tracciamento
  }

  toggleAllSubTables(): void {
    this.allSubTablesExpanded = !this.allSubTablesExpanded;
    this.cd.detectChanges();
  }

  hasAnySubTables(): boolean {
    // SOSTITUISCI generatedTests con rawTestsForDisplay e columns con rawTestsColumns
    if (!this.rawTestsForDisplay || this.rawTestsForDisplay.length === 0 || !this.rawTestsColumns || this.rawTestsColumns.length === 0) {
      return false;
    }
    return this.rawTestsForDisplay.some(test =>
      this.rawTestsColumns.some(columnKey => test && this.isSubTable(test[columnKey]))
    );
  }

  public canEditTests(): boolean {
    // SOSTITUISCI generatedTests con rawTestsForDisplay
    return this.rawTestsForDisplay.length > 0 && !this.isGeneratingTests;
  }

  public getTrueOriginalIdForTestOperations(): string {
    return (this.microScenarioSpec.isDuplicateContent && this.microScenarioSpec.originalMicroScenarioId)
      ? this.microScenarioSpec.originalMicroScenarioId
      : this.microScenarioSpec.id;
  }

  openEditModal(): void {
    if (!this.canEditTests()) {
      this.toastr.info('Nessun test da modificare o generazione in corso.', 'Modifica Non Disponibile');
      return;
    }
    // SOSTITUISCI generatedTests con rawTestsForDisplay
    this.testsForModal = JSON.parse(JSON.stringify(
      this.rawTestsForDisplay.map(t => ({ ...t }))
    ));
    // SOSTITUISCI columns con rawTestsColumns
    this.columnsForModalEditing = [...this.rawTestsColumns];
    this.isEditModalVisible = true;
    this.cd.detectChanges();
  }

  handleModalSave(editedTestsFromModal: any[]): void {
    const trueOriginalIdForPersistence = this.getTrueOriginalIdForTestOperations();
    const macroIdForMetadata = this.microScenarioSpec.macro_id || this.macroScenario.id || 'UNKNOWN_MACRO_ID_MODAL';

    const allGlobalTests = this.sessioneService.getCurrentGeneratedTests();

    const processedTestsForGlobalState: TestWithMetadata[] = [];
    let changesMade = false;

    // Crea una mappa dei test originali per questo micro scenario per un accesso veloce
    const originalGlobalTestsMap = new Map<string, TestWithMetadata>();
    allGlobalTests.forEach(t => {
      if (t.metadata.microScenarioId === trueOriginalIdForPersistence) {
        originalGlobalTestsMap.set(t.metadata.displayId, t);
      }
    });

    // Itera sui test modificati dal modale
    editedTestsFromModal.forEach((editedTest, index) => {
      const newSequentialId = index + 1;
      const originalFrontendId = editedTest._frontendDisplayId_;

      if (originalFrontendId) {
        const originalTestFromGlobal = originalGlobalTestsMap.get(originalFrontendId);

        if (originalTestFromGlobal) {
          // Il test esisteva già, controlla se è stato modificato
          const cleanEditedData = { ...editedTest };
          delete cleanEditedData._frontendDisplayId_;

          const dataChanged = JSON.stringify(originalTestFromGlobal.data) !== JSON.stringify(cleanEditedData);
          if (dataChanged) { changesMade = true; }

          processedTestsForGlobalState.push({
            data: cleanEditedData,
            metadata: {
              ...originalTestFromGlobal.metadata,
              microScenarioId: trueOriginalIdForPersistence, // Assicura che l'ID sia corretto
              sequentialId: newSequentialId // Aggiorna l'ID sequenziale
            }
          });
          // Rimuovi dalla mappa per tenere traccia dei test che sono stati eliminati nel modale
          originalGlobalTestsMap.delete(originalFrontendId);
        } else {
          // Il test era nel modale ma non nel set originale (caso strano, ma gestito)
          // console.warn(`[MicroTestGen ${trueOriginalIdForPersistence}] (ModalSave) WARN: Edited test with _frontendDisplayId_ ${originalFrontendId} not found in original global set.`);
          changesMade = true;
          const cleanNewData = { ...editedTest };
          delete cleanNewData._frontendDisplayId_;
          const newDetails: MicroTestMetadataCreationDetails = {
            microScenarioId: trueOriginalIdForPersistence,
            macroScenarioId: macroIdForMetadata,
            gherkinSection: this.microScenarioSpec.gherkin_section,
            testIndex: index,
            source: 'MicroScenarioTestGenerator_Modal_OrphanEdit'
          };
          const newTestMeta = this.testIdService.createTestWithMetadata(cleanNewData, newDetails);
          newTestMeta.metadata.sequentialId = newSequentialId;
          processedTestsForGlobalState.push(newTestMeta);
        }
      } else {
        // Il test è stato aggiunto nel modale (non ha un _frontendDisplayId_)
        changesMade = true;
        const cleanNewData = { ...editedTest };
        delete cleanNewData._frontendDisplayId_;
        const newTestDetails: MicroTestMetadataCreationDetails = {
          microScenarioId: trueOriginalIdForPersistence,
          macroScenarioId: macroIdForMetadata,
          gherkinSection: this.microScenarioSpec.gherkin_section,
          testIndex: index,
          source: 'MicroScenarioTestGenerator_Modal_New'
        };
        const newTestWithMetadata = this.testIdService.createTestWithMetadata(cleanNewData, newTestDetails);
        newTestWithMetadata.metadata.sequentialId = newSequentialId;
        processedTestsForGlobalState.push(newTestWithMetadata);
      }
    });

    // Se la mappa contiene ancora degli elementi, significa che sono stati cancellati nel modale
    if (originalGlobalTestsMap.size > 0) {
      changesMade = true;
    }

    if (changesMade) {
      // Ricostruisci la lista globale dei test
      const otherGlobalTests = allGlobalTests.filter(
        test => test.metadata.microScenarioId !== trueOriginalIdForPersistence
      );

      const finalGlobalTests = this.testIdService.sortTests([
        ...otherGlobalTests,
        ...processedTestsForGlobalState
      ]);

      // Aggiorna lo stato globale
      this.sessioneService.updateGeneratedTests(finalGlobalTests);
      this.saveSessionAfterChange(`tests updated via modal for micro (linked to ${trueOriginalIdForPersistence})`);
      this.toastr.success('Test edits saved successfully.', 'Edits Saved');

      // Forza l'aggiornamento della vista di questo componente
      // La sottoscrizione a combineLatest si attiverà, ma per sicurezza possiamo chiamare il metodo direttamente.
      const allLatestGlobalTests = this.sessioneService.getCurrentGeneratedTests();
      const relevantTestsForThisInstance = allLatestGlobalTests.filter(
        t => t.metadata && t.metadata.microScenarioId === trueOriginalIdForPersistence
      );
      // **CORREZIONE DELL'ERRORE TS2551**
      this.processExistingRawTests(relevantTestsForThisInstance);

    } else {
      this.toastr.info('No changes detected from modal edit.', 'No Update');
    }

    this.isEditModalVisible = false;
    this.cd.detectChanges();
  }

  handleModalClose(): void {
    this.isEditModalVisible = false;
    this.cd.detectChanges();
  }

  private processConsolidatedTests(relevantRawTests: TestWithMetadata[], allConsolidatedTests: DynamicRefinedTestCase[]): void {
    // 1. Get the micro scenario ID for this component
    const thisMicroScenarioId = this.getTrueOriginalIdForTestOperations();

    // 2. If no raw tests exist for this micro-scenario, then no consolidated tests should be shown.
    if (relevantRawTests.length === 0) {
      if (this.consolidatedTestsForDisplay.length > 0) {
        this.consolidatedTestsForDisplay = [];
        this.consolidatedColumns = [];
        this.cd.detectChanges();
      }
      return;
    }

    // 3. Filter the global list of consolidated tests to find only those relevant to this micro-scenario.
    const relevantConsolidated = allConsolidatedTests.filter(cTest => {
      // Primary Method: Check if this consolidated test was derived from our micro scenario.
      if (cTest.sourceMicroScenarioIds && cTest.sourceMicroScenarioIds.length > 0) {
        return cTest.sourceMicroScenarioIds.includes(thisMicroScenarioId);
      }

      // Fallback for older data: Check if the rationale mentions any of our raw test IDs.
      if (cTest.rationale) {
        const rawTestIds = relevantRawTests.map(t => t.metadata.displayId);
        for (const rawId of rawTestIds) {
          if (cTest.rationale.includes(rawId)) {
            return true;
          }
        }
      }

      return false;
    });

    // 4. Transform the filtered consolidated tests into the TestWithMetadata format that the template expects.
    const newConsolidatedForDisplay = relevantConsolidated.map((dynamicTest, index) => {
      return {
        metadata: {
          displayId: dynamicTest.id,
          testId: dynamicTest.id,
          // Use the globally-sequenced ID from the service.
          sequentialId: dynamicTest.sequentialId,
          source: 'ConsolidatedInMicro',
          gherkinSection: 'when' as const,
          testIndex: index
        },
        data: dynamicTest.data || {}
      };
    });

    // 5. Update the component's state only if the data has actually changed to avoid unnecessary re-renders.
    if (JSON.stringify(this.consolidatedTestsForDisplay) !== JSON.stringify(newConsolidatedForDisplay)) {
      this.consolidatedTestsForDisplay = newConsolidatedForDisplay;

      // 6. Prepare the column headers for the table.
      if (this.consolidatedTestsForDisplay.length > 0) {
        // Get all keys from the 'data' property of the first test.
        const keys = Object.keys(this.consolidatedTestsForDisplay[0].data);

        // *** THE FIX FOR DUPLICATE IDs ***
        // Filter out any key that is 'ID' (case-insensitive) from the original data.
        const filteredKeys = keys.filter(k => k.toUpperCase() !== 'ID');

        // Manually add our special 'ID' column to the very beginning of the headers list.
        // The template will use this special column name to render the ID from the metadata.
        this.consolidatedColumns = ['ID', ...filteredKeys];

      } else {
        this.consolidatedColumns = [];
      }

      this.cd.detectChanges();
    }
  }

  ngOnDestroy(): void {
    const microIdForLogs = this.microScenarioSpec?.id || this.microScenarioSpec?.specification?.substring(0, 30) + '...' || 'unknown';
    this.destroy$.next();
    this.destroy$.complete();
    this.memoizedSubTableColumns.clear();
    this.memoizedSubTableRows.clear();

    // Clean up modal state
    if (this.isFullScreenModalVisible) {
      document.body.style.overflow = '';
    }
  }
}
